import { useEffect, useMemo } from 'preact/hooks';
import { useSearchParams } from "react-router-dom";
import { atom, useAtom, useAtomValue } from 'jotai';
import { loadable } from 'jotai/utils';

export const multiParamToArray = (param) => {
    const values = [];
    for (let key in param) {
        if (param[key]) {
            values.push(key);
        }
    }
    return values;
};

export const multiParamToString = (param) => {
    return multiParamToArray(param).join("|");
};

export const stringToMultiParam = (string) => {
    return arrayToMultiParam(string.split("|").filter(v => v.length));
};

export const arrayToMultiParam = (values) => {
    let param = {};
    for (let value of values) {
        param[value] = true;
    }
    return param;
};

const asLoadable = (loadableValue) => {
    switch (loadableValue.state) {
        case 'hasData':
            return {state: 'hasValue', contents: loadableValue.data};
        case 'hasError':
            return {state: 'hasError', contents: loadableValue.error};
        default:
            return {state: 'loading', contents: undefined};
    }
};

const createSelectParameter = (name, options, multiValue) => {
    const valueState = atom(undefined);

    const optionsState = atom(options);
    const optionsLoadableState = loadable(optionsState);

    const useParameter = () => {
        const [value, valueSetter] = useAtom(valueState);
        const optionsLoadable = useAtomValue(optionsLoadableState);
        const optionsValue = asLoadable(optionsLoadable);

        const [searchParams, setSearchParams] = useSearchParams();

        useEffect(() => {
            if (value === undefined) {
                if (searchParams.get(name) !== null) {
                    const paramValue = searchParams.get(name);
                    valueSetter(multiValue ? stringToMultiParam(paramValue) : paramValue);
                } else if (optionsValue.state === "hasValue") {
                    if (optionsValue.contents?.defaultValue) {
                        valueSetter(optionsValue.contents.defaultValue);
                    } else if (multiValue) {
                        valueSetter({});
                    }
                }
            }
        }, [value, optionsValue, valueSetter, searchParams]);

        const loading = optionsValue.state !== "hasValue" || value === undefined;

        const setter = useMemo(() => ((nextValue) => {
            valueSetter(nextValue);
            const paramValue = multiValue ? multiParamToString(nextValue) : nextValue;
            searchParams.set(name, paramValue);
            setSearchParams(searchParams);
        }), [valueSetter, setSearchParams, searchParams]);

        let returnValue = value;
        if (multiValue && !value) {
            returnValue = {};
        }
        return [returnValue, setter, optionsValue.contents?.options, loading];
    };

    return [valueState, optionsLoadableState, useParameter];
};

export const createSingleSelectParameter = (name, options) => createSelectParameter(name, options, false);
export const createMultiSelectParameter = (name, options) => createSelectParameter(name, options, true);

const createValueParameter = (name, defaultValue, deserializer, serializer) => {
    const valueState = atom(defaultValue);

    const useParameter = () => {
        const [value, valueSetter] = useAtom(valueState);

        const [searchParams, setSearchParams] = useSearchParams();

        useEffect(() => {
            if (searchParams.get(name) !== null) {
                const paramValue = searchParams.get(name);
                valueSetter(deserializer(paramValue));
            }
        });

        const setter = (nextValue) => {
            const searchParamsObject = Object.fromEntries(searchParams);
            setSearchParams({...searchParamsObject, [name]: serializer(nextValue)});
            valueSetter(nextValue);
        };

        return [value, setter];
    };

    return [valueState, useParameter];
}

export const createTextParameter = (value, defaultValue) => createValueParameter(value, defaultValue, v => v, v => v);
export const createBooleanParameter = (value, defaultValue) =>
    createValueParameter(value, defaultValue, v => (v && v !== "false"), v => v.toString());
